/**
 * Drilling down to a named structure without a keyboard.
 *
 * There is no text entry in VR worth using, so the desk build's search box
 * becomes a three-level drill in the headset: systems, then the concepts under
 * a system, then the parts under a concept. This is the pure half of that: it
 * decides what rows to show, what the breadcrumb says, and where a press goes.
 * The window is left with nothing to do but paint it.
 *
 * Paged because a uikit panel has no scroll container to fall back on and the
 * atlas has 3,432 concepts. Rows are a fixed count per page and the markup
 * pre-allocates that many slots.
 */
import type { Catalog, Group } from "../model.js";

export const ROWS_PER_PAGE = 10;

export type BrowseLevel =
  | { kind: "systems" }
  | { kind: "concepts"; system: string }
  | { kind: "parts"; system: string; concept: string | null };

export interface BrowseState {
  level: BrowseLevel;
  page: number;
}

export interface BrowseRow {
  /** Row identity, passed back to `browseInto`. */
  id: string;
  name: string;
  /** Parts this row covers, for the count column. */
  count: number;
  /** What selecting this row selects. */
  group: Group;
  /** False for a leaf: pressing it selects but does not descend. */
  drills: boolean;
}

export function initialBrowse(): BrowseState {
  return { level: { kind: "systems" }, page: 0 };
}

/** Every row at the current level, before paging. */
function allRows(catalog: Catalog, level: BrowseLevel): BrowseRow[] {
  if (level.kind === "systems") {
    // outermost first: skin, then muscle, then bone, which is the order a
    // person peels a body in
    return [...catalog.systems]
      .sort((a, b) => a.layer - b.layer || a.name.localeCompare(b.name))
      .map((s) => ({ id: s.id, name: s.name, count: s.parts.length, group: { kind: "system" as const, id: s.id, name: s.name, parts: s.parts }, drills: true }));
  }

  if (level.kind === "concepts") {
    const system = catalog.systemById.get(level.system);
    if (!system) return [];
    const members = new Set(system.parts);
    const rows: BrowseRow[] = [];
    const claimed = new Set<string>();
    for (const concept of catalog.concepts) {
      const parts = concept.parts.filter((id) => members.has(id));
      if (parts.length < 2) continue; // a one-part concept adds nothing over the part
      for (const id of parts) claimed.add(id);
      rows.push({ id: concept.id, name: concept.name, count: parts.length, group: { kind: "concept", id: concept.id, name: concept.name, parts }, drills: true });
    }
    rows.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
    // parts no multi-part concept claimed would otherwise be unreachable
    const loose = system.parts.filter((id) => !claimed.has(id));
    for (const id of loose) {
      const part = catalog.byId.get(id);
      if (part) rows.push({ id: part.id, name: part.name, count: 1, group: { kind: "part", id: part.id, name: part.name, parts: [part.id] }, drills: false });
    }
    return rows;
  }

  const parts = level.concept === null ? (catalog.systemById.get(level.system)?.parts ?? []) : (catalog.conceptById.get(level.concept)?.parts ?? []);
  return parts
    .map((id) => catalog.byId.get(id))
    .filter((p): p is NonNullable<typeof p> => p !== undefined)
    .map((p) => ({ id: p.id, name: p.name, count: 1, group: { kind: "part" as const, id: p.id, name: p.name, parts: [p.id] }, drills: false }));
}

export function pageCount(catalog: Catalog, state: BrowseState): number {
  return Math.max(1, Math.ceil(allRows(catalog, state.level).length / ROWS_PER_PAGE));
}

/** The rows to paint, at most `ROWS_PER_PAGE` of them. */
export function browseRows(catalog: Catalog, state: BrowseState): BrowseRow[] {
  const rows = allRows(catalog, state.level);
  const page = Math.min(Math.max(0, state.page), Math.max(0, Math.ceil(rows.length / ROWS_PER_PAGE) - 1));
  return rows.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE);
}

/** Where you are, for the breadcrumb. */
export function browseCrumb(catalog: Catalog, state: BrowseState): string {
  if (state.level.kind === "systems") return "All systems";
  const system = catalog.systemById.get(state.level.system);
  if (state.level.kind === "concepts") return system?.name ?? "System";
  const concept = state.level.concept === null ? null : catalog.conceptById.get(state.level.concept);
  return `${system?.name ?? "System"} · ${concept?.name ?? "All parts"}`;
}

/** True when going up is possible; the breadcrumb doubles as the back control. */
export function canGoUp(state: BrowseState): boolean {
  return state.level.kind !== "systems";
}

export function browseUp(state: BrowseState): BrowseState {
  if (state.level.kind === "parts") return { level: { kind: "concepts", system: state.level.system }, page: 0 };
  if (state.level.kind === "concepts") return { level: { kind: "systems" }, page: 0 };
  return state;
}

/**
 * Follow a row. A row that drills descends a level; a leaf leaves the level
 * alone, because selecting a part should not move the list out from under the
 * person who is comparing two of them.
 */
export function browseInto(catalog: Catalog, state: BrowseState, rowId: string): BrowseState {
  const row = allRows(catalog, state.level).find((r) => r.id === rowId);
  if (!row || !row.drills) return state;
  if (state.level.kind === "systems") return { level: { kind: "concepts", system: rowId }, page: 0 };
  if (state.level.kind === "concepts") return { level: { kind: "parts", system: state.level.system, concept: rowId }, page: 0 };
  return state;
}

export function browsePage(catalog: Catalog, state: BrowseState, delta: number): BrowseState {
  const last = pageCount(catalog, state) - 1;
  return { ...state, page: Math.min(last, Math.max(0, state.page + delta)) };
}
